import { Quote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Reveal from "@/components/landing/Reveal";

const testimonials = [
  {
    name: "Ana María G.",
    detail: "Nivel B2 · Estudiante Universitaria",
    text: "Estudiar inglés en el PIO ha sido una gran experiencia. Las clases son muy dinámicas y me ha ayudado a ganar confianza al hablar.",
    image: "https://images.unsplash.com/photo-1622319107576-cca7c8a906f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHx5b3VuZyUyMHN0dWRlbnQlMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTE0MTUwMjZ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Carlos Ramírez",
    detail: "Nivel A2 · Profesional",
    text: "Gracias a los horarios entre semana pude ajustar mi trabajo con el estudio. Los profesores son excelentes y muy pacientes.",
    image: "https://images.pexels.com/photos/27670041/pexels-photo-27670041.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Laura V.",
    detail: "Nivel B2 · Egresada",
    text: "Logré la preparación que necesitaba para mi hoja de vida gracias a la metodología práctica en el nivel avanzado del PIO.",
    image: "https://images.unsplash.com/photo-1515378960530-7c0da6231fb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBvbiUyMGxhcHRvcCUyMHJlbW90ZXxlbnwwfHx8fDE3OTE0MTUwMjZ8MA&ixlib=rb-4.1.0&q=85",
  },
];

const faqs = [
  {
    q: "¿Desde qué edad puedo estudiar inglés?",
    a: "Aceptamos estudiantes desde los 15 años en adelante para nuestros programas generales.",
  },
  {
    q: "¿Qué niveles ofrece el programa?",
    a: "Ofrecemos niveles desde A1 (Básico) hasta B2 (Intermedio Alto) según el Marco Común Europeo de Referencia para las Lenguas.",
  },
  {
    q: "¿Necesito conocimientos previos?",
    a: "No, puedes iniciar desde cero. Si ya tienes conocimientos, realizaremos una prueba de nivelación.",
  },
  {
    q: "¿Cuál es la duración de cada nivel?",
    a: "La duración varía según el nivel y la intensidad horaria del grupo. Contacta a un asesor para conocer los detalles exactos.",
  },
  {
    q: "¿Cómo puedo inscribirme?",
    a: "Puedes llenar el formulario de esta página, escribirnos por WhatsApp o acercarte a nuestra sede.",
  },
];

export default function Testimonials() {
  return (
    <>
      <section id="testimonios" className="py-20 lg:py-32 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">Testimonios</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
              Conoce la experiencia de nuestros estudiantes
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials-grid">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08}>
                <figure
                  data-testid={`testimonial-card-${i + 1}`}
                  className="h-full rounded-2xl border border-slate-200 bg-white p-8 flex flex-col transition-transform hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-900/5"
                >
                  <Quote className="w-7 h-7 text-blue-600/30" />
                  <blockquote className="mt-4 text-sm sm:text-base text-slate-700 leading-relaxed flex-1">
                    “{t.text}”
                  </blockquote>
                  <figcaption className="mt-6 flex items-center gap-4">
                    <img
                      src={t.image}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover border border-slate-200"
                      data-testid={`testimonial-avatar-${i + 1}`}
                    />
                    <div>
                      <div className="font-heading font-semibold text-slate-900 text-sm">{t.name}</div>
                      <div className="text-xs text-slate-500">{t.detail}</div>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="py-20 lg:py-32 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">Preguntas frecuentes</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
              Resuelve tus dudas sobre el programa
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-item-${i + 1}`}>
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i + 1}`}
                    className="text-left font-heading font-semibold text-slate-900 text-base sm:text-lg"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent data-testid={`faq-content-${i + 1}`} className="text-slate-600 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
